import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Check, ExternalLink } from "lucide-react";
import { useSharedLinks } from "@/hooks/useSharedLinks";

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recordId: string;
  recordName: string;
  recordStatus: string;
}

export const ShareDialog = ({
  open,
  onOpenChange,
  recordId,
  recordName,
  recordStatus,
}: ShareDialogProps) => {
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { 
    isLoading, 
    createShare, 
    copyToClipboard, 
    getShareUrl,
    checkCanShare 
  } = useSharedLinks();

  // Проверяем, можно ли поделиться записью (статус должен быть completed)
  const canShare = recordStatus === 'completed';

  const handleCreateShare = async () => {
    if (!canShare) return;
    
    const token = await createShare(recordId);
    if (token) {
      setShareToken(token);
    }
  };

  const handleCopyLink = async () => {
    if (!shareToken) return;
    
    const success = await copyToClipboard(shareToken);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenLink = () => {
    if (shareToken) {
      const url = getShareUrl(shareToken);
      window.open(url, '_blank');
    }
  };

  const resetDialog = () => {
    setShareToken(null);
    setCopied(false);
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        onOpenChange(newOpen);
        if (!newOpen) {
          resetDialog();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Поделиться записью</DialogTitle>
          <DialogDescription>
            Создайте публичную ссылку на запись "{recordName}"
          </DialogDescription>
        </DialogHeader>

        {!canShare ? (
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Поделиться можно только обработанными записями. 
                Дождитесь завершения обработки этой записи.
              </p>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Закрыть
              </Button>
            </div>
          </div>
        ) : !shareToken ? (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Для отмены публичного доступа нажмите на кнопку `поделится` еще раз и отмените публичный доступ
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Отмена
              </Button>
              <Button 
                onClick={handleCreateShare}
                disabled={isLoading}
              >
                {isLoading ? "Создание..." : "Создать ссылку"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="share-link">Ваша ссылка для шаринга</Label>
              <div className="flex space-x-2">
                <Input
                  id="share-link"
                  value={getShareUrl(shareToken)}
                  readOnly
                  className="flex-1"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={handleCopyLink}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={handleOpenLink}
                  className="shrink-0"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                ✅ Ссылка создана! Теперь любой, у кого есть эта ссылка, может просмотреть вашу запись.
              </p>
            </div>

            <div className="text-xs text-muted-foreground">
              <p>• Ссылка бессрочная (не истекает)</p>
              <p>• Доступ без авторизации</p>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => onOpenChange(false)}>
                Готово
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};